import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import AvatarImage from '../components/AvatarImage';
import UploadAvatar from '../components/UploadAvatar';
import axios from 'axios';
import '../styles/Profile.css';
import backendUrl from '../BackendUrl';

const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [editingName, setEditingName] = useState(false);
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const storedToken = localStorage.getItem('authToken');

  const toggleNameEdit = () => {
    setEditingName((prev) => !prev);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${backendUrl}/api/accounts/update`,
        { firstName, lastName },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      // Assuming the API returns the updated user
      setUser(response.data.user);
      setSuccess('Profile updated successfully!');
      setError('');
      setEditingName(false);
    } catch (err) {
      console.error(err);
      setError('Failed to update profile. Please try again.');
      setSuccess('');
    }
  };

  // Component to display spec bars
  const SpecBar = ({ label, value, maxValue, unit }) => {
    const percentage = Math.min((value / maxValue) * 100, 100);
    return (
      <div className="spec-bar">
        <div className="spec-bar-label">{label}: {value}{unit}</div>
        <div className="spec-bar-container">
          <div className="spec-bar-fill" style={{ width: `${percentage}%` }} />
        </div>
      </div>
    );
  };

  // Fetch saved vehicles from the backend when the component mounts
  useEffect(() => {
    const fetchSavedVehicles = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${backendUrl}/api/accounts/getVehicles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedVehicles(response.data.savedVehicles);
      } catch (err) {
        console.error("Error fetching saved vehicles:", err);
      }
    };
    fetchSavedVehicles();
  }, []);

  // Function to delete a saved vehicle
  const handleDeleteVehicle = async (vehicleId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${backendUrl}/api/accounts/deleteVehicle/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Update the local state to remove the deleted vehicle
      setSavedVehicles(savedVehicles.filter(vehicle => vehicle._id !== vehicleId));
      alert("Vehicle deleted successfully!");
    } catch (err) {
      console.error("Error deleting vehicle:", err);
      alert("Error deleting vehicle");
    }
  };

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="profile-page">
      {/* User Info Card */}
      <div className="info-card">
        <div className="banner">
          <div className="avatar-wrap">
            <AvatarImage />
            <UploadAvatar />
          </div>
        </div>
        <div className="info-content">
          <p className="email">{user?.email}</p>
          <h2>{`${user?.firstName} ${user?.lastName}`}</h2>
          {editingName ? (
            <div className="edit-name">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
              />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
              />
              <button onClick={handleSave}>Save Name</button>
              <button className="cancel-btn" onClick={toggleNameEdit}>
                Cancel
              </button>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
            </div>
          ) : (
            <>
              <button onClick={toggleNameEdit}>Change Name</button>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
            </>
          )}
          <button className="password-btn" onClick={() => navigate('/reset-password')}>Change Password</button>
        </div>
      </div>

      {/* Saved Vehicles Card */}
      <div className="vehicles-card">
        <div className="vehicles-header">
          <h2>Saved Vehicles</h2>
        </div>
        <div className="vehicles-content">
          {savedVehicles.length === 0 ? (
            <p>
              You have no saved vehicles. Start your build by following the{' '}
              <a href="/buildnprice">Build and Price</a> link.
            </p>
          ) : (
            <ul>
              {savedVehicles.map((vehicle) => (
                <li key={vehicle._id} className="saved-vehicle">
                  <h3>{vehicle.buildName || "Unnamed Build"}</h3>
                  <p><strong>{vehicle.color} {vehicle.model} {vehicle.trim}</strong></p>
                  <p><strong>Class:</strong> {vehicle.class}</p>

                  <img src={vehicle.savedImg} alt={vehicle.buildName} />

                  <div className="vehicle-spec-bars">
                    <SpecBar label="Combined MPG" value={vehicle.mpgCmb} maxValue={37} unit=" mpg" />
                    <SpecBar label="Mileage" value={vehicle.mileage} maxValue={510} unit=" mi" />
                    <SpecBar label="Weight" value={vehicle.weight} maxValue={3300} unit=" lbs" />
                    <SpecBar label="Seats" value={vehicle.seats} maxValue={9} unit="" />
                  </div>

                  <p><strong>Total Price:</strong> ${Number(vehicle.totalPrice).toLocaleString()}</p>

                  <button
                    className="delete-vehicle-btn"
                    onClick={() => handleDeleteVehicle(vehicle._id)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;